/* Cart drawer add-ons (snippets/st-cart-addons.liquid). Add posts the variant to the cart, asks for the
   cart sections in the same request and announces a CartLinesUpdateEvent, the event Horizon's own
   Add to cart uses, so the drawer redraws itself and stays open. The added product then leaves the
   list, so the confirmation is written into the drawer's status line and focused there. Without
   JavaScript the button submits its form to /cart/add.
   The row scrolls sideways; previous and next page it by the cards that fit, and show only when the
   row is wider than its box, each disabled at its end. */
if (!customElements.get('st-cart-addons')) {
  customElements.define(
    'st-cart-addons',
    class extends HTMLElement {
      connectedCallback() {
        this.#nav();
        this.addEventListener('submit', (event) => {
          const button = event.submitter || event.target.querySelector('[data-st-add]');
          if (!button || !button.dataset.stAdd) return;
          event.preventDefault();
          this.#addVariant(button);
        });
      }

      #nav() {
        const row = this.querySelector('[data-st-row]');
        const nav = this.querySelector('[data-st-nav]');
        const prev = this.querySelector('[data-st-prev]');
        const next = this.querySelector('[data-st-next]');
        if (!row || !nav || !prev || !next) return;
        // Writes only what changed: the observer below watches these attributes, and the drawer's redraw
        // puts back the server's (arrows hidden, previous disabled) whatever the row's scroll.
        const set = (el, name, on) => {
          if (el[name] !== on) el[name] = on;
        };
        const update = () => {
          const max = row.scrollWidth - row.clientWidth;
          set(nav, 'hidden', max <= 1);
          // scrollLeft runs negative in a right-to-left row, so compare its size.
          const at = Math.abs(row.scrollLeft);
          set(prev, 'disabled', at <= 1);
          set(next, 'disabled', at >= max - 1);
        };
        const page = (dir) => {
          const card = row.querySelector('li');
          const gap = parseFloat(getComputedStyle(row).columnGap) || 0;
          const step = card ? card.getBoundingClientRect().width + gap : row.clientWidth;
          const per = Math.max(1, Math.floor((row.clientWidth + gap) / step));
          const rtl = getComputedStyle(row).direction === 'rtl' ? -1 : 1;
          const still = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
          row.scrollBy({ left: dir * rtl * per * step, behavior: still ? 'auto' : 'smooth' });
        };
        prev.addEventListener('click', () => page(-1));
        next.addEventListener('click', () => page(1));
        let frame = 0;
        row.addEventListener(
          'scroll',
          () => {
            cancelAnimationFrame(frame);
            frame = requestAnimationFrame(update);
          },
          { passive: true }
        );
        // The box can change size, and the drawer's redraw can take a card out without changing it.
        new ResizeObserver(update).observe(row);
        new MutationObserver(update).observe(row, { childList: true });
        new MutationObserver(update).observe(this, { subtree: true, attributes: true, attributeFilter: ['hidden', 'disabled'] });
        update();
      }

      async #addVariant(button) {
        // Read the button once: the redraw can reuse this element for another card.
        const id = button.dataset.stAdd;
        const done = button.dataset.done || '';
        // A preorder add-on's form carries the Preorder property (snippets/st-preorder.liquid).
        const properties = {};
        button.form?.querySelectorAll('input[data-st-prop]').forEach((input) => {
          const name = input.name.match(/^properties\[(.+)\]$/)?.[1];
          if (name) properties[name] = input.value;
        });
        const error = this.querySelector('[data-st-error]');
        if (error) error.hidden = true;
        button.disabled = true;
        button.setAttribute('aria-busy', 'true');
        const root = window.Shopify?.routes?.root || '/';
        const json = (r) => {
          if (!r.ok) throw new Error(String(r.status));
          return r.json();
        };
        try {
          const { CartLinesUpdateEvent } = await import('@shopify/events');
          const sections = [...document.querySelectorAll('cart-items-component[data-section-id]')].map((c) => c.dataset.sectionId);
          const added = await fetch(`${root}cart/add.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({
              items: [{ id: Number(id), quantity: 1, ...(Object.keys(properties).length ? { properties } : {}) }],
              ...(sections.length ? { sections: sections.join(','), sections_url: window.location.pathname } : {}),
            }),
          }).then(json);
          const cart = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } }).then(json);
          const deferred = CartLinesUpdateEvent.createPromise();
          this.dispatchEvent(
            new CartLinesUpdateEvent({
              action: 'add',
              context: 'cart',
              lines: [{ merchandiseId: id, quantity: 1 }],
              promise: deferred.promise,
            })
          );
          deferred.resolve({
            cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
            detail: { sections: added.sections, items: cart.items, itemCount: cart.item_count, source: 'st-cart-addons', didError: false },
          });
          // The redraw replaces this list on its own schedule: wait until the button has gone, then
          // say what changed and move focus there (the status line sits outside the list).
          const drawer = () => [...document.querySelectorAll('dialog[open]')].find((d) => d.querySelector('cart-drawer-component'));
          const stillThere = () => drawer()?.querySelector(`[data-st-add="${CSS.escape(id)}"]`);
          for (let waited = 0; stillThere() && waited < 3000; waited += 100) {
            await new Promise((resolve) => setTimeout(resolve, 100));
          }
          await new Promise((resolve) => setTimeout(resolve, 0));
          const status = drawer()?.querySelector('[data-st-addons-status]');
          if (status) {
            status.textContent = done;
            status.focus();
          }
        } catch (e) {
          button.disabled = false;
          button.removeAttribute('aria-busy');
          if (error) error.hidden = false;
        }
      }
    }
  );
}
